class CollectionTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-tab-btn]')];
    this.panels = [...this.querySelectorAll('[data-tab-panel]')];
    this._handleClick = this.handleClick.bind(this);
    this.addEventListener('click', this._handleClick);

    for (const panel of this.panels) {
      this._initPagination(panel);
    }

    const parts = window.location.pathname.split('/');
    const collectionIndex = parts.indexOf('collections');
    const urlHandle = collectionIndex !== -1 ? parts[collectionIndex + 1] : null;
    if (urlHandle) {
      const matchingTab = this.tabs.find(t => t.dataset.collectionHandle === urlHandle);
      if (matchingTab) {
        this.activateTab(matchingTab);
        requestAnimationFrame(() => this._scrollTabIntoBar(matchingTab, false));
      }
    } else {
      const activeTab = this.tabs.find(t => t.classList.contains('is-active'));
      if (activeTab) requestAnimationFrame(() => this._scrollTabIntoBar(activeTab, false));
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
  }

  handleClick(event) {
    const tab = event.target.closest('[data-tab-btn]');
    if (tab) {
      this.activateTab(tab);
      return;
    }

    const prevBtn = event.target.closest('[data-prev-page]');
    if (prevBtn) {
      const panel = prevBtn.closest('[data-tab-panel]');
      if (panel) this._changePage(panel, -1);
      return;
    }

    const nextBtn = event.target.closest('[data-next-page]');
    if (nextBtn) {
      const panel = nextBtn.closest('[data-tab-panel]');
      if (panel) this._changePage(panel, 1);
    }
  }

  _initPagination(panel) {
    const perPage = parseInt(panel.dataset.perPage, 10) || 12;
    const cards = [...panel.querySelectorAll('.collection-tabs-grid .product-card')];
    panel._currentPage = 1;
    panel._perPage = perPage;
    panel._totalCards = cards.length;
    this._renderPage(panel, 1);
  }

  _renderPage(panel, page) {
    const { _perPage: perPage, _totalCards: total } = panel;
    const totalPages = Math.ceil(total / perPage);
    const cards = [...panel.querySelectorAll('.collection-tabs-grid .product-card')];
    const start = (page - 1) * perPage;
    const end = start + perPage;

    panel._currentPage = page;

    for (let i = 0; i < cards.length; i++) {
      cards[i].classList.toggle('is-hidden', i < start || i >= end);
    }

    const pagination = panel.querySelector('[data-pagination]');
    if (!pagination) return;

    if (totalPages <= 1) {
      pagination.setAttribute('hidden', '');
      return;
    }

    pagination.removeAttribute('hidden');

    const pageInfo = pagination.querySelector('[data-page-info]');
    const prevBtn = pagination.querySelector('[data-prev-page]');
    const nextBtn = pagination.querySelector('[data-next-page]');

    if (pageInfo) pageInfo.textContent = `${page} / ${totalPages}`;
    if (prevBtn) prevBtn.disabled = page <= 1;
    if (nextBtn) nextBtn.disabled = page >= totalPages;
  }

  _changePage(panel, delta) {
    const totalPages = Math.ceil(panel._totalCards / panel._perPage);
    const newPage = Math.min(Math.max(panel._currentPage + delta, 1), totalPages);
    if (newPage === panel._currentPage) return;
    this._renderPage(panel, newPage);
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _scrollTabIntoBar(tab, animate) {
    const tabsBar = tab.closest('.collection-tabs-bar') || tab.parentElement;
    const barRect = tabsBar.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const target = tabsBar.scrollLeft + (tabRect.left - barRect.left) - (barRect.width / 2) + (tabRect.width / 2);
    if (!animate) {
      tabsBar.scrollLeft = target;
      return;
    }
    const start = tabsBar.scrollLeft;
    const distance = target - start;
    const duration = 300;
    const startTime = performance.now();
    function step(now) {
      const t = Math.min((now - startTime) / duration, 1);
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      tabsBar.scrollLeft = start + distance * ease;
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  activateTab(activeTab) {
    this._scrollTabIntoBar(activeTab, true);
    const targetId = activeTab.dataset.blockId;

    for (const tab of this.tabs) {
      const isActive = tab.dataset.blockId === targetId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    }

    for (const panel of this.panels) {
      const isActive = panel.dataset.tabPanel === targetId;
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
        this._renderPage(panel, 1);
      } else {
        panel.setAttribute('hidden', '');
      }
    }
  }
}

if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}
